import { useCallback, useMemo } from "react"
import {
  isOrganizationStaff,
  PARTNER_ADMIN_ROLE,
} from "../helpers/staffRoleHelpers"

export default function useAdditionalServices({
  staffRole,
  toggles,
  navigate,
  menuModel = [],
  menuItemTemplate,
  onOpenDocuments,
  onOpenHousing,
}) {
  // Shared core for both renderers:
  // 1) build app-defined service entries from role/toggles
  // 2) merge them into configured Services when present
  // 3) expose canonical merged items for any UI surface

  // Opens Documents through the renderer-provided callback.
  const openDocuments = useCallback(() => {
    if (typeof onOpenDocuments === "function") {
      onOpenDocuments()
    }
  }, [onOpenDocuments])

  // Opens Housing through the renderer-provided callback.
  const openHousing = useCallback(() => {
    if (typeof onOpenHousing === "function") {
      onOpenHousing()
    }
  }, [onOpenHousing])

  // Build the Additional Services list once per relevant role/toggle change.
  const additionalServicesItems = useMemo(() => {
    const items = []

    // Helper keeps item shape consistent across all entries.
    const addItem = (label, command) => {
      items.push({
        label,
        command,
      })
    }

    // Coordination Boards is always available.
    addItem("Coordination Boards", () => navigate("/coordination-board"))

    // Extra services are available only below Partner Admin role.
    if (staffRole < PARTNER_ADMIN_ROLE) {
      if (toggles.documentsEnabled) {
        addItem("Documents", openDocuments)
      }

      if (toggles.telerecovery) {
        addItem("External Calls", () => navigate("/external-calls"))
      }

      if (toggles.housingEnabled) {
        addItem("Housing", openHousing)
      }

      if (toggles.telerecovery) {
        addItem("Messages", () => navigate("/global-messages"))
      }

      if (isOrganizationStaff(staffRole, true)) {
        addItem("Referral Network", () => navigate("/referral-network"))
      }
    }

    return items
  }, [
    navigate,
    openDocuments,
    openHousing,
    staffRole,
    toggles.documentsEnabled,
    toggles.housingEnabled,
    toggles.telerecovery,
  ])

  // Nav/Main renderer can provide a template; sidebar renderer can skip it.
  const additionalServicesItemsWithTemplate = useMemo(() => {
    if (!menuItemTemplate) {
      return additionalServicesItems
    }

    return additionalServicesItems.map((item) => ({
      ...item,
      template: menuItemTemplate,
    }))
  }, [additionalServicesItems, menuItemTemplate])

  // Canonical top-level Services group produced by this hook.
  const additionalServicesMenuItem = useMemo(
    () => ({
      label: "Services",
      icon: "settings",
      className: "nav-additional-services-item",
      template: menuItemTemplate,
      items: additionalServicesItemsWithTemplate,
    }),
    [additionalServicesItemsWithTemplate, menuItemTemplate],
  )

  const menuModelWithAdditionalServices = useMemo(() => {
    // Keep configured Services position, append only missing app-defined entries.
    // This preserves admin-configured order while guaranteeing app-required items.
    const servicesIndex = menuModel.findIndex(
      (row) => row.label === additionalServicesMenuItem.label,
    )

    if (servicesIndex >= 0) {
      // Merge into existing Services group if one already exists in config.
      const configuredServicesItem = menuModel[servicesIndex]
      const configuredServiceItems = Array.isArray(configuredServicesItem.items)
        ? configuredServicesItem.items
        : []

      // De-dupe by label so shared items are not inserted twice.
      const mergedServiceItems = [...configuredServiceItems]
      additionalServicesMenuItem.items.forEach((serviceItem) => {
        const alreadyExists = mergedServiceItems.some(
          (existingItem) => existingItem?.label === serviceItem?.label,
        )

        if (!alreadyExists) {
          mergedServiceItems.push(serviceItem)
        }
      })

      const updatedServicesItem = {
        ...configuredServicesItem,
        icon: configuredServicesItem?.icon || additionalServicesMenuItem?.icon,
        items: mergedServiceItems,
      }

      return menuModel.map((item, index) =>
        index === servicesIndex ? updatedServicesItem : item,
      )
    }

    // If config has no Services group, append the hook-defined one.
    return [...menuModel, additionalServicesMenuItem]
  }, [additionalServicesMenuItem, menuModel])

  // Single source of truth for renderer parity:
  // both top-nav and sidebar should consume this merged Services children list.
  const mergedAdditionalServicesItems = useMemo(() => {
    const mergedServicesGroup = menuModelWithAdditionalServices.find(
      (item) => item?.label === additionalServicesMenuItem.label,
    )

    return Array.isArray(mergedServicesGroup?.items)
      ? mergedServicesGroup.items
      : []
  }, [additionalServicesMenuItem.label, menuModelWithAdditionalServices])

  // Expose only outputs consumed by current renderers.
  return {
    mergedAdditionalServicesItems,
    menuModelWithAdditionalServices,
  }
}
